import { createContext, useContext, useReducer, useEffect, useCallback, type ReactNode } from 'react';
import { api } from '../services/api';
import { adminLLMApi } from '../services/api/admin';
import type { LLMProviderSettings, JailbreakPresetListItem, JailbreakPreset } from '../services/types';

// ============================================================
// State
// ============================================================

interface AIConsoleState {
  // Provider
  providers: LLMProviderSettings[];
  selectedProviderId: string;
  isLoadingProviders: boolean;
  // Parameters
  maxTokens: number;
  temperature: number;
  enableCache: boolean;
  maxIterations: number;
  // Presets
  presets: JailbreakPresetListItem[];
  selectedPresetId: string;
  isLoadingPresets: boolean;
}

const initialState: AIConsoleState = {
  providers: [],
  selectedProviderId: '',
  isLoadingProviders: true,
  maxTokens: 10000,
  temperature: 0.3,
  enableCache: true,
  maxIterations: 10,
  presets: [],
  selectedPresetId: '',
  isLoadingPresets: true,
};

// ============================================================
// Actions
// ============================================================

type AIConsoleAction =
  | { type: 'SET_PROVIDERS'; providers: LLMProviderSettings[] }
  | { type: 'SET_PROVIDERS_LOADING'; loading: boolean }
  | { type: 'SET_SELECTED_PROVIDER'; id: string }
  | { type: 'SET_MAX_TOKENS'; value: number }
  | { type: 'SET_TEMPERATURE'; value: number }
  | { type: 'SET_ENABLE_CACHE'; value: boolean }
  | { type: 'SET_MAX_ITERATIONS'; value: number }
  | { type: 'SET_PRESETS'; presets: JailbreakPresetListItem[] }
  | { type: 'SET_PRESETS_LOADING'; loading: boolean }
  | { type: 'SET_SELECTED_PRESET'; id: string };

function reducer(state: AIConsoleState, action: AIConsoleAction): AIConsoleState {
  switch (action.type) {
    case 'SET_PROVIDERS':
      return {
        ...state,
        providers: action.providers,
        isLoadingProviders: false,
        selectedProviderId: state.selectedProviderId || (action.providers[0]?.provider_id ?? ''),
      };
    case 'SET_PROVIDERS_LOADING':
      return { ...state, isLoadingProviders: action.loading };
    case 'SET_SELECTED_PROVIDER':
      return { ...state, selectedProviderId: action.id };
    case 'SET_MAX_TOKENS':
      return { ...state, maxTokens: action.value };
    case 'SET_TEMPERATURE':
      return { ...state, temperature: action.value };
    case 'SET_ENABLE_CACHE':
      return { ...state, enableCache: action.value };
    case 'SET_MAX_ITERATIONS':
      return { ...state, maxIterations: action.value };
    case 'SET_PRESETS':
      return { ...state, presets: action.presets, isLoadingPresets: false };
    case 'SET_PRESETS_LOADING':
      return { ...state, isLoadingPresets: action.loading };
    case 'SET_SELECTED_PRESET':
      return { ...state, selectedPresetId: action.id };
    default:
      return state;
  }
}

// ============================================================
// Context
// ============================================================

interface AIConsoleContextValue extends AIConsoleState {
  setSelectedProviderId: (id: string) => void;
  setMaxTokens: (v: number) => void;
  setTemperature: (v: number) => void;
  setEnableCache: (v: boolean) => void;
  setMaxIterations: (v: number) => void;
  setSelectedPresetId: (id: string) => void;
  loadPreset: () => Promise<JailbreakPreset | null>;
  reloadPresets: () => Promise<void>;
  reloadProviders: () => Promise<void>;
}

const AIConsoleContext = createContext<AIConsoleContextValue | null>(null);

// ============================================================
// Provider
// ============================================================

export function AIConsoleProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const loadProviders = useCallback(async () => {
    try {
      dispatch({ type: 'SET_PROVIDERS_LOADING', loading: true });
      const allProviders = await api.getLLMProviders();
      const filtered = allProviders.filter(
        (p) => p.api_format === 'anthropic' && p.is_enabled
      );
      dispatch({ type: 'SET_PROVIDERS', providers: filtered });
    } catch {
      dispatch({ type: 'SET_PROVIDERS_LOADING', loading: false });
    }
  }, []);

  const loadPresets = useCallback(async () => {
    try {
      dispatch({ type: 'SET_PRESETS_LOADING', loading: true });
      const data = await adminLLMApi.listJailbreakPresets();
      dispatch({ type: 'SET_PRESETS', presets: data });
    } catch {
      dispatch({ type: 'SET_PRESETS_LOADING', loading: false });
    }
  }, []);

  useEffect(() => {
    loadProviders();
    loadPresets();
  }, [loadProviders, loadPresets]);

  const loadPreset = useCallback(async (): Promise<JailbreakPreset | null> => {
    if (!state.selectedPresetId) return null;
    return await adminLLMApi.getJailbreakPreset(state.selectedPresetId);
  }, [state.selectedPresetId]);

  const value: AIConsoleContextValue = {
    ...state,
    setSelectedProviderId: (id) => dispatch({ type: 'SET_SELECTED_PROVIDER', id }),
    setMaxTokens: (value) => dispatch({ type: 'SET_MAX_TOKENS', value }),
    setTemperature: (value) => dispatch({ type: 'SET_TEMPERATURE', value }),
    setEnableCache: (value) => dispatch({ type: 'SET_ENABLE_CACHE', value }),
    setMaxIterations: (value) => dispatch({ type: 'SET_MAX_ITERATIONS', value }),
    setSelectedPresetId: (id) => dispatch({ type: 'SET_SELECTED_PRESET', id }),
    loadPreset,
    reloadPresets: loadPresets,
    reloadProviders: loadProviders,
  };

  return (
    <AIConsoleContext.Provider value={value}>
      {children}
    </AIConsoleContext.Provider>
  );
}

export function useAIConsole(): AIConsoleContextValue {
  const ctx = useContext(AIConsoleContext);
  if (!ctx) {
    throw new Error('useAIConsole must be used within AIConsoleProvider');
  }
  return ctx;
}
